// Settings dialog opened from the account menu.
import { useCallback, useId } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useT } from '@/i18n/useT';
import {
  useBreakpoint,
  isPhoneBreakpoint,
} from '@/hooks/ui/useBreakpoint.js';
import AccountSettingsSection from '@/features/settings/components/AccountSettingsSection';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   returnFocusRef?: import('react').RefObject<HTMLElement | null>,
 * }} props
 */
export default function SettingsDialog({ open, onClose, returnFocusRef }) {
  const t = useT();
  const titleId = useId();
  const currentBreakpoint = useBreakpoint();
  const isSmallScreen = isPhoneBreakpoint(currentBreakpoint);

  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() => returnFocusRef?.current?.focus());
  }, [returnFocusRef]);

  const handleClose = useCallback(() => {
    onClose();
    restoreFocus();
  }, [onClose, restoreFocus]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: isSmallScreen ? 0 : 2 } }}
    >
      <DialogTitle id={titleId} sx={{ fontWeight: 700, pr: 6 }}>
        {t('settings.title', 'Settings')}
        <IconButton
          aria-label={t('settings.close', 'Close settings')}
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <AccountSettingsSection />
      </DialogContent>
    </Dialog>
  );
}
